import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  Stack,
  Radio,
  Flex,
  useColorModeValue
} from '@chakra-ui/react';
import { useLyricsTheme } from '../context/LyricsThemeContext';
import TemplatesPanel from './TemplatesPanel';

interface LyricsSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  layoutMode: 'split' | 'tesla';
  setLayoutMode: (mode: 'split' | 'tesla') => void;
}

const LyricsSettingsModal: React.FC<LyricsSettingsModalProps> = ({
  isOpen,
  onClose,
  layoutMode,
  setLayoutMode
}) => {
  // Get what we need from the theme context
  const { 
    isSpotifyTheme,
    templates,
    currentTemplate,
    saveTemplate,
    deleteTemplate,
    applyTemplate,
    setDefaultTemplate
  } = useLyricsTheme();

  // UI colors
  const accentColor = isSpotifyTheme ? 'green' : 'pink';
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'white');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  const sectionHeadingColor = useColorModeValue(`${accentColor}.600`, `${accentColor}.300`);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent 
        bg={bgColor} 
        borderWidth="1px" 
        borderColor={borderColor} 
        color={textColor}
        boxShadow="xl"
      >
        <ModalHeader 
          fontWeight="bold" 
          color={sectionHeadingColor}
          borderBottomWidth="1px"
          borderColor={borderColor}
          pb={3}
        >
          Lyrics Display Settings
        </ModalHeader>
        <ModalCloseButton color={mutedTextColor} />
        <ModalBody>
          {/* Show only the Templates Panel */}
          <VStack spacing={6} align="stretch">
            <TemplatesPanel
              templates={templates}
              currentTemplate={currentTemplate}
              saveTemplate={saveTemplate}
              deleteTemplate={deleteTemplate}
              applyTemplate={applyTemplate}
              setDefaultTemplate={setDefaultTemplate}
              accentColor={accentColor}
            />
            
            {/* Layout Mode selector at the bottom of the template tab */}
            <Box mt={4} pt={4} borderTopWidth="1px" borderColor={borderColor}>
              <FormControl id="layoutMode">
                <FormLabel fontWeight="bold">Layout Mode</FormLabel>
                <RadioGroup value={layoutMode} onChange={(value: 'split' | 'tesla') => setLayoutMode(value)}>
                  <Stack direction="column" spacing={2}>
                    <Radio value="split">
                      <Flex align="center">
                        <Text fontSize="md" mr={2}>Split View</Text>
                        <Text fontSize="xs" color="gray.500">(Album + Lyrics side by side)</Text>
                      </Flex>
                    </Radio>
                    <Radio value="tesla">
                      <Flex align="center">
                        <Text fontSize="md" mr={2}>Full Width</Text>
                        <Text fontSize="xs" color="gray.500">(Full screen lyrics with expanded controls)</Text>
                      </Flex>
                    </Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter borderTopWidth="1px" borderColor={borderColor}>
          <Button 
            onClick={onClose} 
            colorScheme={accentColor} 
            size="md" 
            borderRadius="md"
          >
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LyricsSettingsModal; 